'use client';

import type { ComponentProps } from 'react';

import {
  FavouritesButton,
  FavouritesMark,
  FavouritesMarkVariant,
} from '@vs-shop/commerce-ui/favouritesButton';
import {
  ProductPanelTool,
  ProductPanelToolLabel,
  ProductPanelToolVariant,
  productPanelToolMarkVariants,
} from '@vs-shop/commerce-ui/productPanelTools';
import { clsxm } from '@vs-shop/utils';
import { Heart, HeartPlus } from 'lucide-react';
import { useIntl } from 'react-intl';

type Props = ComponentProps<'button'> & {
  variant: 'card' | 'detail';
  isFavourite?: boolean | undefined;
};

export default function ProductFavouritesButton({ variant, isFavourite, ...rest }: Props) {
  const intl = useIntl();

  // Already a favourite is the tool that UNDOES rather than does, and the mark
  // and the word answer to the same target — so the appearance is read once and
  // handed to both.
  const toolVariant =
    isFavourite === true ? ProductPanelToolVariant.DESTRUCTIVE : ProductPanelToolVariant.DEFAULT;

  if (variant === 'card') {
    return (
      <FavouritesButton
        aria-label={intl.formatMessage({
          id: 'favourites.button.label',
          defaultMessage: 'Oblíbené',
        })}
        {...rest}
      >
        <FavouritesMark
          variant={
            isFavourite === true ? FavouritesMarkVariant.SAVED : FavouritesMarkVariant.UNSAVED
          }
          render={<Heart />}
        />
      </FavouritesButton>
    );
  }

  // The panel's placement is a tool in the row beneath the buy box, so it is
  // the shared tool part — the same target the compare and price-history tools
  // draw. How big the drawing reads is this call site's; what colour it takes
  // is the row's, which is what the mark recipe spells.
  return (
    <ProductPanelTool {...rest}>
      <HeartPlus
        className={clsxm('size-5.5', productPanelToolMarkVariants({ variant: toolVariant }))}
      />
      <ProductPanelToolLabel variant={toolVariant}>
        {isFavourite === true
          ? intl.formatMessage({
              id: 'favourites.updateFavourites',
              defaultMessage: 'Upravit v oblíbených',
            })
          : intl.formatMessage({
              id: 'favourites.addToFavourites',
              defaultMessage: 'Přidat do oblíbených',
            })}
      </ProductPanelToolLabel>
    </ProductPanelTool>
  );
}
